const SELLING_PLAN_FIELD_NAME = 'selling_plan';
const STORAGE_KEY = 'selectedSellingPlan';
const PLAN_CHANGE_EVENT = 'planChange';
const LEGACY_WIDGET_SELECTOR = '#appstle_subscription_widget0';

// Bridge inline change handlers with the custom element event flow.
function handleSellingPlanChange(event) {
  const component = event.target.closest('selling-plans');
  if (component) {
    component.dispatchEvent(new CustomEvent(PLAN_CHANGE_EVENT));
  }
}

// Remove third-party subscription widgets that clash with the native component.
function removeOldSellingPlanWidget() {
  setTimeout(() => {
    const widget = document.querySelector(LEGACY_WIDGET_SELECTOR);
    if (widget) {
      console.log('Removing old subscription widget:', widget);
      widget.remove();
    }
  }, 500);
}

removeOldSellingPlanWidget();

if (!customElements.get('selling-plans')) {
  class SellingPlans extends HTMLElement {
    constructor() {
      super();
      this.handlePlanChange = this.handlePlanChange.bind(this);
    }

    connectedCallback() {
      this.cacheDomReferences();
      this.addEventListener(PLAN_CHANGE_EVENT, this.handlePlanChange);
      this.restoreSelection();
    }

    disconnectedCallback() {
      this.removeEventListener(PLAN_CHANGE_EVENT, this.handlePlanChange);
    }

    cacheDomReferences() {
      this.radios = this.querySelectorAll('[data-radio]');
      this.tabs = this.querySelectorAll('[data-tab]');
      this.productInfo = this.productInfo || this.closest('product-info') || document;
      const scope = this.productInfo || document;
      this.form = scope.querySelector('[data-type="add-to-cart-form"]') || document.querySelector('[data-type="add-to-cart-form"]');
      this.productVariantId = this.form?.querySelector('.product-variant-id') || null;
      this.regularPrice = scope.querySelector('[data-regular-price]') || null;
      this.salePrice = scope.querySelector('[data-sale-price]') || null;
      this.priceBadges = scope.querySelectorAll('[data-price-badge]') || null;
    }

    handlePlanChange() {
      this.cacheDomReferences();
      this.radios.forEach((radio) => {
        if (!radio.checked) return;
        this.persistSelection(radio);
        this.toggleTabs(radio);
        this.syncVariant(radio);
        this.updatePriceDisplay(radio);
      });
      removeOldSellingPlanWidget();
    }

    persistSelection(radio) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ type: radio.dataset.radio }));
    }

    toggleTabs(radio) {
      if (!this.tabs.length) return;
      this.tabs.forEach((tab) => tab.removeAttribute('data-open'));
      const targetTab = this.querySelector(`[data-tab="${radio.dataset.radio}"]`);
      if (targetTab) {
        targetTab.setAttribute('data-open', '');
      }
    }

    syncVariant(radio) {
      if (!this.form || !this.productVariantId) return;

      if (radio.dataset.radio === 'subscriptions') {
        const input = this.getOrCreateSellingPlanInput();
        input.value = radio.dataset.subscription;
        input.setAttribute('data-selected-selling-plan', radio.dataset.subscription);
      } else {
        const existingInput = this.form.querySelector(`input[name="${SELLING_PLAN_FIELD_NAME}"]`);
        if (existingInput) {
          existingInput.remove();
        }
      }

      this.productVariantId.value = radio.dataset.variantId;
    }

    getOrCreateSellingPlanInput() {
      let input = this.form.querySelector(`input[name="${SELLING_PLAN_FIELD_NAME}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = SELLING_PLAN_FIELD_NAME;
        this.form.prepend(input);
      }
      return input;
    }

    restoreSelection() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { type } = JSON.parse(saved) || {};
          const radio = type ? this.querySelector(`[data-radio="${type}"]`) : null;
          if (radio) {
            radio.checked = true;
          }
        }
      } catch (error) {
        console.warn('Failed to restore selling plan selection:', error);
      }
      this.handlePlanChange();
    }

    // Swap between sale and regular price states based on the selected plan metadata.
    updatePriceDisplay(radio) {
      const scope = this.productInfo || document;
      this.regularPrice = this.regularPrice || scope.querySelector('[data-regular-price]');
      this.salePrice = this.salePrice || scope.querySelector('[data-sale-price]');

      if (!this.regularPrice && !this.salePrice) return;

      const normalPriceRaw = radio.dataset.normalPrice;
      const compareAtPriceRaw = radio.dataset.compareAtPrice;
      const normalPrice =
        typeof normalPriceRaw === 'string' ? normalPriceRaw.trim() : normalPriceRaw;
      const compareAtPrice =
        typeof compareAtPriceRaw === 'string' ? compareAtPriceRaw.trim() : compareAtPriceRaw;
      if (!normalPrice) return;

      const hasCompareAtPrice =
        Boolean(compareAtPrice) && compareAtPrice !== '' && compareAtPrice !== normalPrice;

      // Check if sale price element actually exists in DOM (not just cached)
      let salePriceExists = scope.querySelector('[data-sale-price]') !== null;

      // If sale price element doesn't exist and we have a compare at price, create it
      if (hasCompareAtPrice && !salePriceExists && this.regularPrice) {
        const priceContainer = this.regularPrice.parentElement;
        if (priceContainer && priceContainer.classList.contains('custom-price-item__prices')) {
          this.salePrice = document.createElement('div');
          this.salePrice.className = 'price__sale';
          this.salePrice.setAttribute('data-sale-price', '');

          // Insert after regular price element but before product tax element
          const productTax = priceContainer.querySelector('[data-product-tax]');
          if (productTax) {
            priceContainer.insertBefore(this.salePrice, productTax);
          } else {
            priceContainer.appendChild(this.salePrice);
          }

          salePriceExists = true;
          // Update cache reference
          this.cacheDomReferences();
        }
      }

      // Set visibility attributes
      if (this.regularPrice) {
        this.regularPrice.dataset.regularPrice = !hasCompareAtPrice;
      }
      if (this.salePrice) {
        this.salePrice.dataset.salePrice = hasCompareAtPrice;
      }

      if (!hasCompareAtPrice) {
        // No compare-at price: render regular price and hide sale price if it exists
        this.renderRegularPrice(normalPrice);
        if (this.salePrice) {
          this.salePrice.dataset.salePrice = 'false';
        }
        return;
      }

      // Has compare-at price: keep regular price element unchanged (just hide it)
      // and render sale price in the sale price element
      this.renderSalePrice(normalPrice, compareAtPrice);
    }

    renderRegularPrice(value) {
      if (!this.regularPrice) return;
      this.clearElement(this.regularPrice);
      this.regularPrice.appendChild(
        this.createPriceNode(value, {
          tag: 'span',
          className: 'price-item price-item--regular'
        })
      );

      // Adjust price badges margin based on width difference
      if (this.priceBadges && this.priceBadges.length) {
        requestAnimationFrame(() => {
          const wrapperWidth = this.regularPrice.parentElement?.offsetWidth || 0;
          const priceItemWidth = this.regularPrice.offsetWidth || 0;
          const offset = wrapperWidth - priceItemWidth;
          this.priceBadges.forEach(badge => {
            badge.style.marginLeft = `-${offset}px`;
          });
        });
      }
    }

    renderSalePrice(normalPrice, compareAtPrice) {
      if (!this.salePrice) return;
      this.clearElement(this.salePrice);

      const compareWrapper = document.createElement('span');
      compareWrapper.appendChild(
        this.createPriceNode(compareAtPrice, {
          tag: 's',
          className: 'price-item price-item--regular'
        })
      );
      this.salePrice.appendChild(compareWrapper);

      this.salePrice.appendChild(
        this.createPriceNode(normalPrice, {
          tag: 'span',
          className: 'price-item price-item--sale price-item--last'
        })
      );

      // Reset price badges margin
      if (this.priceBadges && this.priceBadges.length) {
        this.priceBadges.forEach(badge => {
          badge.style.marginLeft = '0';
        });
      }
    }

    renderSalePriceInRegular(normalPrice, compareAtPrice) {
      if (!this.regularPrice) return;
      this.clearElement(this.regularPrice);

      // Keep data-regular-price="true" so it doesn't get hidden
      this.regularPrice.dataset.regularPrice = 'true';

      const compareWrapper = document.createElement('span');
      compareWrapper.appendChild(
        this.createPriceNode(compareAtPrice, {
          tag: 's',
          className: 'price-item price-item--regular'
        })
      );
      this.regularPrice.appendChild(compareWrapper);

      this.regularPrice.appendChild(
        this.createPriceNode(normalPrice, {
          tag: 'span',
          className: 'price-item price-item--sale price-item--last'
        })
      );

      // Adjust price badges margin
      if (this.priceBadges && this.priceBadges.length) {
        requestAnimationFrame(() => {
          const wrapperWidth = this.regularPrice.parentElement?.offsetWidth || 0;
          const priceItemWidth = this.regularPrice.offsetWidth || 0;
          const offset = wrapperWidth - priceItemWidth;
          this.priceBadges.forEach(badge => {
            badge.style.marginLeft = `-${offset}px`;
          });
        });
      }
    }

    createPriceNode(value, { tag, className }) {
      const node = document.createElement(tag);
      if (className) {
        node.className = className;
      }
      node.textContent = value;
      return node;
    }

    clearElement(element) {
      while (element && element.firstChild) {
        element.innerHTML = '';
      }
    }
  }

  customElements.define('selling-plans', SellingPlans);
}
